import React from "react";
import { useLocation, Link } from "react-router-dom";
import SEO from "../components/SEO";
import { useI18n } from "../contexts/I18nContext";

export default function OrderConfirmPage() {
  const { t } = useI18n();
  const location = useLocation();
  const order = location.state?.order;

  if (!order) {
    return (
      <div className="dept-access-page">
        <SEO title={t.order_seoTitle} path="/order-confirm" />
        <div className="dept-access-page__inner">
          <div className="dept-access-page__mark" aria-hidden="true">ORDER</div>
          <div className="dept-access-page__copy">
            <span className="dept-access-page__eyebrow">주문 안내</span>
            <h1>{t.order_notFound}</h1>
            <p>완료된 주문 화면은 결제 직후에 확인할 수 있습니다.</p>
            <div className="dept-access-page__actions">
              <Link to="/store" className="dept-access-page__primary">{t.order_browse}</Link>
              <Link to="/cart" className="dept-access-page__secondary">장바구니 보기</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f5f5f7] min-h-screen">
      <SEO title={t.order_seoTitle} path="/order-confirm" />

      <div className="max-w-[600px] mx-auto px-4 pt-16 pb-20">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto bg-[#34c759]/10 rounded-full flex items-center justify-center mb-4">
            <svg className="w-10 h-10 text-[#34c759]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <h1 className="text-[28px] font-semibold text-[#1d1d1f] tracking-tight">{t.order_success}</h1>
          <p className="text-[15px] text-[#6e6e73] mt-2">{t.order_successSub}</p>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100/80 mb-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <span className="text-[13px] text-[#6e6e73]">{t.order_orderNo}</span>
              <span className="text-[15px] font-bold text-[#1d1d1f] font-mono">{order.order_number}</span>
            </div>
            {order.total_amount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-[#6e6e73]">{t.order_amount}</span>
                <span className="text-[20px] font-bold text-[#0071e3]">{order.total_amount.toLocaleString()}{t.order_won}</span>
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100/80 mb-6">
          <h2 className="text-[15px] font-semibold text-[#1d1d1f] mb-4">{t.order_guideTitle}</h2>
          <div className="space-y-3">
            {[
              { step: "1", title: t.order_step1, desc: t.order_step1Desc, done: true },
              { step: "2", title: t.order_step2, desc: t.order_step2Desc },
              { step: "3", title: t.order_step3, desc: t.order_step3Desc },
              { step: "4", title: t.order_step4, desc: t.order_step4Desc },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[12px] font-bold ${
                  s.done ? "bg-[#34c759] text-white" : "bg-[#f5f5f7] text-[#86868b]"
                }`}>
                  {s.done ? "✓" : s.step}
                </div>
                <div>
                  <p className={`text-[14px] font-medium ${s.done ? "text-[#34c759]" : "text-[#1d1d1f]"}`}>{s.title}</p>
                  <p className="text-[12px] text-[#86868b]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 교환/반품 안내 */}
        <div className="bg-[#f5f5f7] rounded-2xl p-5 mb-6 border border-gray-200/60">
          <p className="text-[13px] text-[#6e6e73] mb-1">교환/반품은 상품 수령 후 7일 이내 신청 가능합니다.</p>
          <Link to="/policy" className="text-[13px] text-[#0071e3] font-medium hover:underline">교환/반품 정책 자세히 보기 →</Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/my"
            className="flex-1 py-3.5 bg-[#0071e3] text-white text-center rounded-xl font-semibold text-[15px] hover:bg-[#005bb5] transition">
            {t.order_viewHistory}
          </Link>
          <Link to="/store"
            className="flex-1 py-3.5 bg-white text-[#0071e3] text-center rounded-xl font-semibold text-[15px] border-2 border-[#0071e3] hover:bg-[#0071e3]/5 transition">
            {t.order_continueShopping}
          </Link>
        </div>
      </div>
    </div>
  );
}
